import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import VeritageLogo from '../components/VeritageLogo';
import WaveLines from '../components/WaveLines';
import { ArrowRight, Check, Palette } from '../components/Icons';

const DEFAULT_BRANDING = { name: '', logo_url: '', background_url: '' };

export default function BrandingScreen() {
  const [branding, setBranding] = useState(DEFAULT_BRANDING);
  const [saving,   setSaving]   = useState(false);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const [success,  setSuccess]  = useState(null);

  useEffect(() => {
    api.getVerificationConfig()
      .then(cfg => setBranding({ ...DEFAULT_BRANDING, ...(cfg?.branding || {}) }))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const set = (key, val) => setBranding(b => ({ ...b, [key]: val }));

  const save = async () => {
    setSaving(true); setError(null); setSuccess(null);
    try {
      // Merge branding into the existing verification_config
      const existing = await api.getVerificationConfig().catch(() => ({}));
      await api.updateVerificationConfig({ ...existing, branding });
      setSuccess('Branding saved.');
    } catch (e) {
      setError(e.message || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="vt-muted" style={{ padding: '24px 0' }}>Loading…</p>;

  const hasPreview = branding.logo_url || branding.name;

  return (
    <div>
      <div className="vt-pageHead vt-pageHeadRow">
        <div>
          <div className="vt-eyebrow">Verification screen</div>
          <h1 className="vt-pageTitle">Branding</h1>
          <p className="vt-pageSub">
            Customise how the Veritage verification screen looks to your users.
            Your logo and name appear at the top; Veritage attribution is always shown at the bottom.
          </p>
        </div>
        <button className="vt-btn vt-btn--primary vt-btn--lg" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save branding'}
          <span className="vt-btnArrow"><ArrowRight size={13} /></span>
        </button>
      </div>

      {error   && <div className="vt-alert vt-alert--error">{error}</div>}
      {success && <div className="vt-alert vt-alert--success"><Check size={16} /> {success}</div>}

      <div className="vt-grid2" style={{ alignItems: 'start', gap: 20 }}>
        <section className="vt-panel" style={{ marginTop: 0 }}>
          <div className="vt-panelHead">
            <div>
              <h2 className="vt-panelTitle">Your brand</h2>
              <p className="vt-panelSub">Changes apply to every new session as soon as you save.</p>
            </div>
            <span className="vt-tile"><Palette /></span>
          </div>

          <div style={{ display: 'grid', gap: 20 }}>
            <Field label="Company name" hint="Displayed next to your logo on the verification screen.">
              <input className="vt-input" value={branding.name} onChange={e => set('name', e.target.value)} placeholder="Acme Corp" />
            </Field>

            <Field label="Logo URL" hint="PNG or SVG with a transparent background. Shown at up to 36px tall.">
              <input className="vt-input" value={branding.logo_url} onChange={e => set('logo_url', e.target.value)} placeholder="https://yoursite.com/logo.png" />
              {branding.logo_url && (
                <ImgPreview src={branding.logo_url} alt="Logo preview" style={{ maxHeight: 36, marginTop: 4, alignSelf: 'flex-start' }} />
              )}
            </Field>

            <Field label="Background image URL" hint="Shown behind the verification card. A dark or blurred image works best. Optional.">
              <input className="vt-input" value={branding.background_url} onChange={e => set('background_url', e.target.value)} placeholder="https://yoursite.com/bg.jpg" />
              {branding.background_url && (
                <ImgPreview src={branding.background_url} alt="Background preview" style={{ width: '100%', height: 90, objectFit: 'cover', borderRadius: 14, marginTop: 4 }} />
              )}
            </Field>
          </div>
        </section>

        <section className="vt-panel" style={{ marginTop: 0, padding: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px 14px' }}>
            <div className="vt-sectionLabel" style={{ margin: 0 }}>Live preview</div>
            <span className="vt-tag vt-tag--brand vt-tag--dot">identity.veritage.io</span>
          </div>
          <PreviewCard branding={branding} hasPreview={hasPreview} />
          <p className="vt-fieldHint" style={{ textAlign: 'center', margin: '12px 0 6px' }}>
            This is how your users will see the verification screen.
          </p>
        </section>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="vt-field">
      <span className="vt-fieldLabel">{label}</span>
      {children}
      {hint && <span className="vt-fieldHint">{hint}</span>}
    </label>
  );
}

function ImgPreview({ src, alt, style }) {
  const [broken, setBroken] = useState(false);
  useEffect(() => { setBroken(false); }, [src]);
  if (broken) return (
    <span style={{ fontSize: 13, color: '#dc2626' }}>Could not load image — check the URL.</span>
  );
  return <img src={src} alt={alt} style={style} onError={() => setBroken(true)} />;
}

// Mini replica of the verify app's Shell card (matches identity.veritage.io)
function PreviewCard({ branding, hasPreview }) {
  const hasBg = !!(branding.background_url);
  return (
    <div style={{
      position: 'relative',
      borderRadius: 18,
      overflow: 'hidden',
      background: hasBg
        ? `url(${branding.background_url}) center/cover`
        : 'radial-gradient(70% 60% at 0% 0%, #DBD3FF 0%, rgba(219,211,255,0) 70%), radial-gradient(60% 50% at 100% 100%, rgba(219,211,255,0.7) 0%, rgba(219,211,255,0) 70%), #fff',
      padding: '34px 18px',
      minHeight: 340,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      isolation: 'isolate',
    }}>
      {!hasBg && (
        <WaveLines lines={22} amplitude={70} opacity={0.55} style={{ position: 'absolute', top: '32%', left: '-10%', width: '120%', height: '40%', zIndex: -1 }} />
      )}
      <div style={{
        background: 'rgba(255,255,255,0.96)',
        borderRadius: 22,
        padding: '22px 20px 14px',
        width: '100%',
        maxWidth: 290,
        boxShadow: '0 30px 60px -30px rgba(27,15,59,0.35), 0 0 0 1px rgba(0,0,0,0.04)',
      }}>
        {hasPreview && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingBottom: 12, marginBottom: 14, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
            {branding.logo_url && (
              <img src={branding.logo_url} alt={branding.name} style={{ height: 22, maxWidth: 90, objectFit: 'contain' }} onError={() => {}} />
            )}
            {branding.name && <span style={{ fontSize: 13, fontWeight: 500 }}>{branding.name}</span>}
          </div>
        )}
        <p style={{ margin: '0 0 6px', fontFamily: 'var(--vt-font-label)', fontSize: 9, letterSpacing: 1, textTransform: 'uppercase', color: '#6904F2' }}>Identity verification</p>
        <p style={{ margin: '0 0 4px', fontSize: 19, fontWeight: 400, letterSpacing: '-0.4px', color: '#000' }}>Verify your identity</p>
        <p style={{ margin: '0 0 14px', fontSize: 11, color: 'rgba(0,0,0,0.55)' }}>Choose your digital wallet to get started.</p>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', border: '1px solid rgba(0,0,0,0.1)', borderRadius: 12, padding: '10px 10px 10px 12px', marginBottom: 12 }}>
          <span>
            <span style={{ display: 'block', fontSize: 12, fontWeight: 500 }}>EU Digital Identity Wallet</span>
            <span style={{ display: 'block', fontSize: 10, color: 'rgba(0,0,0,0.5)' }}>EU</span>
          </span>
          <span style={{ display: 'grid', placeItems: 'center', width: 22, height: 22, borderRadius: '50%', background: '#000', color: '#fff' }}>
            <ArrowRight size={11} />
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, paddingTop: 10, borderTop: '1px solid rgba(0,0,0,0.06)', fontFamily: 'var(--vt-font-label)', fontSize: 8.5, letterSpacing: 0.8, textTransform: 'uppercase', color: 'rgba(0,0,0,0.45)' }}>
          <span>Powered by</span>
          <VeritageLogo height={12} />
        </div>
      </div>
    </div>
  );
}
